import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import {
  ShieldCheck, CheckCircle2, AlertCircle, ExternalLink, RefreshCw,
  IndianRupee, Globe, Building2, FileText,
} from "lucide-react";

/**
 * Sprint 4 — Gateway Approval Readiness checklist.
 *
 * Admin-only dashboard that aggregates everything a payment-gateway review
 * team (Razorpay for Factskoot/INR, Stripe for Destineze/foreign) wants
 * to see before approving a live account. Each group has its own progress
 * bar and direct links to the policy pages, pricing and contact details.
 */
export default function GatewayReviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/billing/gateway-readiness")
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e.response?.data?.detail || "Could not load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="gateway-review-root">
        <header className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] font-bold flex items-center gap-2">
              <ShieldCheck className="h-3 w-3" /> Gateway Review Mode
            </p>
            <h1 className="mt-1 text-2xl font-bold text-[#0A192F]">Razorpay &amp; Stripe approval readiness</h1>
            <p className="mt-1 text-sm text-slate-500">
              Pre-flight checklist used to demonstrate compliance during gateway onboarding.
            </p>
          </div>
          <button
            type="button" onClick={load}
            className="px-3 py-1.5 rounded-md bg-[#0F4C81] text-white text-xs font-semibold flex items-center gap-1.5"
            data-testid="gateway-review-refresh"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </header>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
        )}
        {loading && !data && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-sm text-slate-500">
            Loading checklist…
          </div>
        )}

        {data && (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <StatusPill
                title="Razorpay (Factskoot · INR)"
                live={data.razorpay_keys_live}
                icon={IndianRupee}
                description={data.razorpay_keys_live
                  ? "Razorpay keys configured — orders go to the live gateway."
                  : "Razorpay running in mock mode. Set RAZORPAY_KEY_ID / SECRET to switch to live."}
              />
              <StatusPill
                title="Stripe (Destineze · Foreign currency)"
                live={data.stripe_keys_live}
                icon={Globe}
                description={data.stripe_keys_live
                  ? "Stripe key configured — international checkout goes to Stripe."
                  : "Stripe key not detected in this environment."}
              />
            </div>

            <Section
              title="Common readiness"
              tone="slate"
              summary={data.common}
              testid="checklist-common"
            />

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <Section
                title="Razorpay — Factskoot (India / INR)"
                tone="gold" entity="factskoot"
                summary={data.razorpay}
                testid="checklist-razorpay"
              />
              <Section
                title="Stripe — Destineze (International)"
                tone="blue" entity="destineze"
                summary={data.stripe}
                testid="checklist-stripe"
              />
            </div>

            <LinksCard links={data.links} />

            <EntitySeparationCard />
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatusPill({ title, live, icon: Icon, description }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-start gap-3">
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${live ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-sm font-semibold text-[#0A192F]">{title}</p>
        <p className={`text-xs ${live ? "text-emerald-700" : "text-amber-700"} font-medium`}>
          {live ? "LIVE" : "TEST / MOCK"}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      </div>
    </div>
  );
}

function Section({ title, tone, summary, testid, entity }) {
  const pct = Math.round((summary.passed / Math.max(summary.total, 1)) * 100);
  const accent = tone === "gold" ? "border-[#D4AF37] bg-[#FFFBEB]"
                : tone === "blue" ? "border-[#0F4C81] bg-blue-50"
                : "border-slate-200 bg-white";
  return (
    <div className={`mt-6 rounded-2xl border ${accent} p-5`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
          <Building2 className="h-4 w-4" /> {title}
        </h3>
        <span className="text-xs font-bold text-[#0A192F]" data-testid={`${testid}-progress`}>
          {summary.passed} / {summary.total} · {pct}%
        </span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div
          className={`h-full ${pct === 100 ? "bg-emerald-500" : pct >= 60 ? "bg-[#D4AF37]" : "bg-amber-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <ul className="mt-3 divide-y divide-slate-100">
        {summary.items.map((it) => (
          <li
            key={it.key}
            className="flex items-start gap-2 py-2 text-xs"
            data-testid={`checklist-item-${it.key}`}
          >
            {it.passed ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
            ) : (
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
            )}
            <span className={it.passed ? "text-slate-700" : "text-amber-800 font-medium"}>
              {it.label}
            </span>
          </li>
        ))}
      </ul>
      {entity && (
        <div className="mt-3 pt-3 border-t border-slate-200 text-[11px] text-slate-600">
          {entity === "factskoot" ? (
            <p>
              <strong>Factskoot Eduwellness Private Limited</strong> · No. 43, WeWork
              Galaxy, Shanthala Nagar, Residency Road, Bengaluru 560025 · info@factskoot.com
              · +91-80-62177852 · WhatsApp +91 9844139333
            </p>
          ) : (
            <p>
              <strong>Destineze Educational Voyages FZCO</strong> · IFZA Business Park,
              Dubai Silicon Oasis, Dubai, UAE · info@destineze.com · Mobile / WhatsApp
              +971 50 488 3656
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function LinksCard({ links }) {
  const entries = [
    { key: "pricing", label: "Pricing" },
    { key: "terms", label: "Terms" },
    { key: "privacy", label: "Privacy Policy" },
    { key: "refund_policy", label: "Refund Policy" },
    { key: "cancellation_policy", label: "Cancellation Policy" },
    { key: "cookie_policy", label: "Cookie Policy" },
    { key: "billing_notice", label: "Billing Notice" },
    { key: "shipping_policy", label: "Digital Delivery / Shipping" },
    { key: "contact", label: "Contact / Grievance" },
  ];
  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="gateway-review-links">
      <h3 className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
        <FileText className="h-4 w-4" /> Public artefacts (for gateway reviewers)
      </h3>
      <p className="text-xs text-slate-500 mt-0.5">
        These pages must be publicly accessible without login.
      </p>
      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
        {entries.map((e) => (
          <Link
            key={e.key}
            to={links[e.key]}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 hover:bg-slate-50"
            data-testid={`gateway-link-${e.key}`}
          >
            <span className="text-[#0A192F] font-medium">{e.label}</span>
            <ExternalLink className="h-3 w-3 text-slate-400" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function EntitySeparationCard() {
  return (
    <div className="mt-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5" data-testid="entity-separation-card">
      <h3 className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" /> Entity separation — enforced server-side
      </h3>
      <ul className="mt-2 space-y-1 text-xs text-emerald-900 list-disc pl-5">
        <li><strong>INR → Factskoot + Razorpay only.</strong> Server refuses Stripe for INR.</li>
        <li><strong>USD/AED/EUR/GBP/HKD → Destineze + Stripe only.</strong> Server refuses Razorpay for foreign.</li>
        <li><strong>Invoice templates are entity-locked.</strong> Factskoot template cannot render a Destineze invoice, and vice versa.</li>
        <li><strong>Refund routing is entity-locked.</strong> Razorpay refunds via Factskoot only, Stripe refunds via Destineze only.</li>
        <li><strong>Tax shape is entity-locked.</strong> Indian GST never on Destineze; UAE VAT never on Factskoot.</li>
        <li><strong>Resolution HMAC.</strong> Every paid transaction stores an HMAC of (entity, gateway, currency, tax_profile); verify / webhook / refund re-check it.</li>
      </ul>
    </div>
  );
}
